import axios from "axios";
import { API_URL } from "@/config";
import { convertToEntityWithInstitution } from "./dtos";

const store = {
  namespaced: true,

  state: {
    freeDays: [],

    preferedGroupedPriceListItemsModalMode:
      JSON.parse(localStorage.getItem("preferedGroupedPriceListItemsModalMode")) || "ungrouped",

    // Used mainly for IncreaseFundsModal.vue
    lastSelectedChildId: JSON.parse(localStorage.getItem("lastSelectedChildId")) || null,
  },

  getters: {
    freeDays: (state) => state.freeDays,
    lastSelectedChildId: (state) => state.lastSelectedChildId,
  },

  mutations: {
    setFreeDays(state, freeDays) {
      state.freeDays = freeDays;
    },

    setPreferedGroupedPriceListItemsModalMode(state, preferedGroupedPriceListItemsModalMode) {
      localStorage.setItem(
        "preferedGroupedPriceListItemsModalMode",
        JSON.stringify(preferedGroupedPriceListItemsModalMode),
      );

      state.preferedGroupedPriceListItemsModalMode = preferedGroupedPriceListItemsModalMode;
    },

    revokePreferedGroupedPriceListItemsModalMode() {
      localStorage.removeItem("preferedGroupedPriceListItemsModalMode");
    },

    setLastSelectedChildId(state, lastSelectedChildId) {
      localStorage.setItem("lastSelectedChildId", JSON.stringify(lastSelectedChildId));

      state.lastSelectedChildId = lastSelectedChildId;
    },

    clearLastSelectedChildId() {
      localStorage.removeItem("lastSelectedChildId");
    },

    // Mirrors clearLastSelectedChildId - Vuex mutations can't call other mutations, so the
    // localStorage cleanup is repeated here.
    resetState(state) {
      localStorage.removeItem("lastSelectedChildId");

      state.freeDays = [];
      state.lastSelectedChildId = null;
    },
  },

  actions: {
    async getOrdersCountForDate(_, { days, criteria = {} }) {
      let data = {
        criteria,
        days,
      };

      const res = await axios.post(`${API_URL}/orders/count`, data);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async searchOrdersCountGroupedByDates(_, criteria) {
      const res = await axios.post(`${API_URL}/orders/countGroupedByDates/search`, criteria);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getFreeDays(_, criteria) {
      const res = await axios.get(`${API_URL}/calendar/freeDays`, { params: criteria });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async searchFreeDays(_, criteria) {
      const res = await axios.post(`${API_URL}/calendar/freeDays/search`, criteria);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async createFreeDays(_, days) {
      const res = await axios.post(`${API_URL}/calendar/freeDays`, days);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async createFreeDaysFromRange(_, freeDaysRangeData) {
      const res = await axios.post(`${API_URL}/calendar/freeDays/range`, freeDaysRangeData);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async removeFreeDays(_, days) {
      const res = await axios.delete(`${API_URL}/calendar/freeDays`, { data: days });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getCalendarDays(_, { subjectId, subjectType, criteria }) {
      const res = await axios.get(`${API_URL}/calendar/days/${subjectType}/${subjectId}`, {
        params: criteria,
      });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getCalendarDaysCount(_, { subjectId, subjectType, criteria }) {
      const res = await axios.get(`${API_URL}/calendar/days/${subjectType}/${subjectId}/count`, {
        params: criteria,
      });

      if (!res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getCalendarDaySelectedMeals(_, { subjectId, subjectType, date }) {
      const res = await axios.get(`${API_URL}/calendar/days/${subjectType}/${subjectId}/${date}`);

      return res.data;
    },

    async markPresences(
      _,
      { subjectId, subjectType, presencesData, addManipulationFee = false, manipulationFee = null },
    ) {
      const res = await axios.post(
        `${API_URL}/calendar/days/${subjectType}/${subjectId}/presences`,
        {
          calendarDaysData: presencesData,
          addManipulationFee: addManipulationFee,
          manipulationFee: manipulationFee,
        },
      );

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async markAbsences(_, { subjectId, subjectType, absencesData }) {
      const res = await axios.post(
        `${API_URL}/calendar/days/${subjectType}/${subjectId}/absences`,
        {
          calendarDaysData: absencesData,
        },
      );

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async bulkMarkPresences(_, days) {
      const res = await axios.post(`${API_URL}/calendar/bulkPresences/mark`, days);

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async bulkMarkPresencesForRange(_, presencesRangeData) {
      const res = await axios.post(
        `${API_URL}/calendar/bulkPresences/markRange`,
        presencesRangeData,
      );

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getSubjects(_, { filters = {}, perPage = 20, pageNumber = 1 }) {
      let criteria = {
        ...filters,
        perPage: perPage,
        page: pageNumber,
      };

      const res = await axios.get(`${API_URL}/calendar/days/subjects`, { params: criteria });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      const items = res.data.items.map((item) => convertToEntityWithInstitution(item));

      return {
        ...res.data,
        items,
      };
    },

    async markPresencesWithDefaultDietAndMealType(_, { subjectId, subjectType, days }) {
      const res = await axios.post(
        `${API_URL}/calendar/days/${subjectType}/${subjectId}/presences/default`,
        days,
      );

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getCalendarSubjectSettingsForSubject(_, { subjectId, subjectType }) {
      const response = await axios.get(
        `${API_URL}/calendar/subjectsSettings/${subjectType}/${subjectId}`,
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async updateCalendarSubjectSettingsForSubject(
      _,
      { subjectId, subjectType, calendarSubjectSettingsData },
    ) {
      const response = await axios.put(
        `${API_URL}/calendar/subjectsSettings/${subjectType}/${subjectId}`,
        calendarSubjectSettingsData,
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async updatePreferedGroupedPriceListItemsModalMode({ commit }) {
      let preferedGroupedPriceListItemsModalMode =
        JSON.parse(localStorage.getItem("preferedGroupedPriceListItemsModalMode")) || "ungrouped";

      commit("setPreferedGroupedPriceListItemsModalMode", preferedGroupedPriceListItemsModalMode);
    },

    async getCalendarEventsForDatesRange(_, { dateFrom, dateTo, criteria = {} }) {
      const response = await axios.get(`${API_URL}/calendar/days/events`, {
        params: { dateFrom, dateTo, ...criteria },
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },
  },
};

export default store;
